import { createTheme, responsiveFontSizes } from '@mui/material/styles';

const pxToRem = (value) => `${value / 16}rem`;

const palette = {
  primary: {
    lighter: '#D0ECFE',
    light: '#73BAFB',
    main: '#1877F2',
    dark: '#0C44AE',
    darker: '#042174',
    contrastText: '#FFFFFF',
  },
  secondary: {
    lighter: '#EFD6FF',
    light: '#C684FF',
    main: '#8E33FF',
    dark: '#5119B7',
    darker: '#27097A',
    contrastText: '#FFFFFF',
  },
  info: {
    lighter: '#CAFDF5',
    light: '#61F3F3',
    main: '#00B8D9',
    dark: '#006C9C',
    darker: '#003768',
    contrastText: '#FFFFFF',
  },
  success: {
    lighter: '#D3FCD2',
    light: '#77ED8B',
    main: '#22C55E',
    dark: '#118D57',
    darker: '#065E49',
    contrastText: '#FFFFFF',
  },
  warning: {
    lighter: '#FFF5CC',
    light: '#FFD666',
    main: '#FFAB00',
    dark: '#B76E00',
    darker: '#7A4100',
    contrastText: '#1C252E',
  },
  error: {
    lighter: '#FFE9D5',
    light: '#FFAC82',
    main: '#FF5630',
    dark: '#B71D18',
    darker: '#7A0916',
    contrastText: '#FFFFFF',
  },
  grey: {
    50: '#FCFDFD',
    100: '#F9FAFB',
    200: '#F4F6F8',
    300: '#DFE3E8',
    400: '#C4CDD5',
    500: '#919EAB',
    600: '#637381',
    700: '#454F5B',
    800: '#1C252E',
    900: '#141A21',
  },
  common: { black: '#000000', white: '#FFFFFF' },
};

const typography = {
  fontFamily: '"DM Sans Variable", "DM Sans", "Roboto", "Helvetica", "Arial", sans-serif',
  fontSecondaryFamily: '"Barlow", "DM Sans Variable", "DM Sans", "Roboto", "Helvetica", "Arial", sans-serif',
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightSemiBold: 600,
  fontWeightBold: 700,
  h1: {
    fontFamily: '"Barlow", "DM Sans Variable", "DM Sans", "Roboto", "Helvetica", "Arial", sans-serif',
    fontWeight: 800,
    lineHeight: 1.25,
    fontSize: pxToRem(40),
  },
  h2: {
    fontFamily: '"Barlow", "DM Sans Variable", "DM Sans", "Roboto", "Helvetica", "Arial", sans-serif',
    fontWeight: 800,
    lineHeight: 1.33,
    fontSize: pxToRem(32),
  },
  h3: {
    fontFamily: '"Barlow", "DM Sans Variable", "DM Sans", "Roboto", "Helvetica", "Arial", sans-serif',
    fontWeight: 700,
    lineHeight: 1.5,
    fontSize: pxToRem(24),
  },
  h4: {
    fontWeight: 700,
    lineHeight: 1.5,
    fontSize: pxToRem(20),
  },
  h5: {
    fontWeight: 700,
    lineHeight: 1.5,
    fontSize: pxToRem(18),
  },
  h6: {
    fontWeight: 600,
    lineHeight: 1.55,
    fontSize: pxToRem(17),
  },
  subtitle1: {
    fontWeight: 600,
    lineHeight: 1.5,
    fontSize: pxToRem(16),
  },
  subtitle2: {
    fontWeight: 600,
    lineHeight: 1.57,
    fontSize: pxToRem(14),
  },
  body1: {
    lineHeight: 1.5,
    fontSize: pxToRem(16),
  },
  body2: {
    lineHeight: 1.57,
    fontSize: pxToRem(14),
  },
  caption: {
    lineHeight: 1.5,
    fontSize: pxToRem(12),
  },
  overline: {
    fontWeight: 700,
    lineHeight: 1.5,
    fontSize: pxToRem(12),
    textTransform: 'uppercase',
  },
  button: {
    fontWeight: 700,
    lineHeight: 1.7,
    fontSize: pxToRem(14),
    textTransform: 'unset',
  },
};

const createAppTheme = (mode = 'light') => {
  const isDark = mode === 'dark';

  let theme = createTheme({
    palette: {
      mode,
      ...palette,
      divider: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(145, 158, 171, 0.2)',
      text: {
        primary: isDark ? '#FFFFFF' : palette.grey[800],
        secondary: isDark ? palette.grey[400] : palette.grey[600],
        disabled: isDark ? palette.grey[500] : palette.grey[500],
      },
      background: {
        default: isDark ? '#0B0F14' : palette.grey[100],
        paper: isDark ? '#111827' : '#FFFFFF',
      },
      action: {
        hover: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(145, 158, 171, 0.08)',
        selected: isDark ? 'rgba(255, 255, 255, 0.16)' : 'rgba(145, 158, 171, 0.16)',
        focus: isDark ? 'rgba(255, 255, 255, 0.24)' : 'rgba(145, 158, 171, 0.24)',
        disabled: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(145, 158, 171, 0.8)',
        disabledBackground: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(145, 158, 171, 0.24)',
        hoverOpacity: 0.08,
        disabledOpacity: 0.48,
      },
    },
    typography,
    shape: { borderRadius: 12 },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: { borderRadius: 16 },
        },
      },
    },
  });

  theme = responsiveFontSizes(theme);

  return theme;
};

export default createAppTheme;
